import { CompanyAvatar } from "@eworkit/iris";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { LazyImg } from "@/components/Fetcher";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { fetchPublicProfile, IPublicProfilesFollowedData } from "@/data/public-profiles";
import { Flex } from "@/eworkitComponents";
import { useFormats } from "@/hooks/Formats";
import { useFetcher } from "@/hooks/http";
import { RouteSection } from "@/infrastructure/Route";
import { router } from "@/views/router";

import { FollowButton } from "../FollowButton";

import "./_client-list-record.scss";

interface IClientRecordProps {
  client: IPublicProfilesFollowedData;
  onRecordUpdate: () => void;
}

export const ClientRecord: FC<IClientRecordProps> = memo(({ client, onRecordUpdate }) => {
  const { t } = useTranslation("public");
  const { formatNumber } = useFormats();

  const { payload } = useFetcher(fetchPublicProfile, { id: client.publicProfileId });

  if (!payload) {
    return null;
  }

  const baseClass = "client-list-record";
  return (
    <RouteSection flex borderRadius path={router.get("client.details", { slug: payload.slug }).path}>
      <Flex direction="column" padding={["lg"]} innerMargin="sm">
        <Flex direction="row" innerMargin="sm">
          <Flex grow={1} direction="row" innerMargin="md">
            <LazyImg systemId={payload.logo.systemId}>
              {({ payload: logoPayload }) => (
                <CompanyAvatar size="lg" img={logoPayload?.publicUrl} name={payload.name} />
              )}
            </LazyImg>

            <Flex direction="column" innerMargin="xs">
              <Header size="md" weight="bold">
                {payload.name}
              </Header>
              <TextListWithDot>
                <Text>
                  {formatNumber(client.jobRequestCount)} {t("CLIENT_PAGE.JOBS_TITLE")}
                </Text>
                <Text>
                  {formatNumber(client.followersCount)} {t("CLIENT_PAGE.FOLLOWERS")}
                </Text>
              </TextListWithDot>
            </Flex>
          </Flex>
          <Flex grow={0} direction="row" justify="flex-start" className={`${baseClass}__button-area`}>
            <FollowButton publicProfileId={client.publicProfileId} onUpdate={onRecordUpdate} />
          </Flex>
        </Flex>
      </Flex>
    </RouteSection>
  );
});
